"use client";

/**
 * ThemePicker — floating panel with NO overlay, NO darkening.
 * The app stays fully interactive behind the panel so you can
 * check theme changes live on every part of the UI.
 *
 * Features:
 *  - Dock to left | right | top | bottom
 *  - Master font-size slider (80 – 140 %)
 *  - Live theme preview with no page block
 *
 * Usage:
 *   <ThemePicker open={open} onClose={() => setOpen(false)} />
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Check,
  ChevronDown,
  Download,
  Film,
  Globe,
  Loader2,
  Monitor,
  Moon,
  Package,
  Palette,
  PanelBottom,
  PanelLeft,
  PanelRight,
  PanelTop,
  Plus,
  RefreshCw,
  Search,
  Share2,
  Shuffle,
  Sparkles,
  Sun,
  Trash2,
  Type,
  Users,
  Triangle,
  X,
  Accessibility,
  Wheat,
  Music,
  Leaf,
  Trophy,
  Headphones,
  type LucideIcon,
} from "lucide-react";
import { VectorPatternGenerator } from "@/components/themes/vector-pattern-generator";
import type { GifEntry, GifSection, GifSizeMode, GifSkin } from "@/contexts/theme-context";
import { GIF_SECTIONS } from "@/contexts/theme-context";
import { cn } from "@/lib/utils";
import {
  themePresets,
  THEME_CATEGORIES,
  type ThemeCategory,
} from "@/lib/theme-presets";
import { useTheme } from "@/contexts/theme-context";
import { useAuth } from "@/contexts/auth-context";
import { useMenuSide } from "@/contexts/user-layout-prefs-context";
import { uploadFile, getStoragePath } from "@/services/storage";
import { CustomThemeBuilder } from "./custom-theme-builder";
import { getPublicGifPacks, getAllGifPacks, saveGifPack, deleteGifPack, type GifThemePack } from "@/services/gif-packs";

// ─── Panel position type ────────────────────────────────────────────────────
type PanelSide = "left" | "right" | "top" | "bottom";

const SIDE_ICONS: Record<PanelSide, React.ReactNode> = {
  left: <PanelLeft className="h-3.5 w-3.5" />,
  right: <PanelRight className="h-3.5 w-3.5" />,
  top: <PanelTop className="h-3.5 w-3.5" />,
  bottom: <PanelBottom className="h-3.5 w-3.5" />,
};

// ─── Font scale stored in localStorage ─────────────────────────────────────
const LS_FONT_KEY = "bb-font-scale";
const LS_SIDE_KEY = "bb-theme-panel-side";

function applyFontScale(scale: number) {
  document.documentElement.style.setProperty("--bb-font-scale", String(scale));
  document.documentElement.style.fontSize = `${scale * 16}px`;
}

// ─── Button shape stored in localStorage ────────────────────────────────────
const LS_BTN_RADIUS_KEY = "bb-btn-radius";

const BTN_SHAPE_PRESETS = [
  { label: "Sharp",    value: "2px",    preview: "rounded-none" },
  { label: "Standard",value: "6px",    preview: "rounded-md" },
  { label: "Rounded", value: "12px",   preview: "rounded-xl" },
  { label: "Pill",    value: "9999px", preview: "rounded-full" },
] as const;

function applyBtnRadius(value: string) {
  document.documentElement.style.setProperty("--btn-radius", value);
}

// ─── Swatch preview ────────────────────────────────────────────────────────
function ThemeSwatch({
  preset,
  isActive,
  onSelect,
  isLoading,
}: {
  preset: (typeof themePresets)[number];
  isActive: boolean;
  onSelect: () => void;
  isLoading: boolean;
}) {
  const c = preset.tokens.colors;
  const menuSide = useMenuSide();
  const bg = `hsl(${c.background})`;
  const fg = `hsl(${c.foreground})`;
  const primary = `hsl(${c.primary})`;
  const accent = `hsl(${c.accent})`;
  const sidebar = `hsl(${c.sidebarBackground})`;

  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onSelect}
            disabled={isLoading}
            className={cn(
              "group relative flex flex-col gap-1.5 p-2 rounded-xl border-2 transition-all text-left w-full",
              "hover:scale-[1.02] hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              isActive
                ? "border-primary shadow-md shadow-primary/20"
                : "border-border/50 hover:border-border"
            )}
          >
            {/* Mini app mockup — today's layout (Gordon 2026-11-01): header with search
                and the menu button, the menu column on the person's menu side (right by
                default), the VU strip on the other edge, hero, a row of square covers and
                the one-row player with the chat button. Mirrors for menu-on-the-left. */}
            <div
              className={cn("relative w-full aspect-[4/3] rounded-lg overflow-hidden flex", menuSide === "left" && "flex-row-reverse")}
              style={{ background: bg }}
            >
              {/* VU strip */}
              <div className="w-1 h-full flex flex-col justify-end" style={{ background: `hsl(${c.card})` }}>
                <div className="h-2/3" style={{ background: `linear-gradient(to top, ${accent}, ${primary})`, opacity: 0.85 }} />
              </div>
              <div className="flex-1 min-w-0 flex flex-col">
                {/* Header: search square · message line */}
                <div className={cn("h-3 flex items-center gap-1 px-0.5", menuSide === "left" && "flex-row-reverse")}>
                  <div className="w-2.5 h-2.5 rounded-[1px]" style={{ border: `1px solid ${fg}`, opacity: 0.8 }} />
                  <div className="h-0.5 w-1/2 rounded" style={{ background: fg, opacity: 0.45 }} />
                </div>
                {/* Hero with a title */}
                <div className="mx-0.5 rounded-sm flex-[1.3] flex flex-col justify-end gap-0.5 p-1"
                  style={{ background: `linear-gradient(135deg, ${primary}, hsl(${c.card}))` }}>
                  <div className="h-1 w-3/4 rounded" style={{ background: fg, opacity: 0.9 }} />
                  <div className="h-1 w-1/2 rounded" style={{ background: fg, opacity: 0.6 }} />
                </div>
                {/* Releases: a row of square covers */}
                <div className="flex gap-0.5 px-0.5 py-1">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="flex-1 aspect-square rounded-[2px]"
                      style={{ background: i === 0 ? primary : `hsl(${c.card})`, border: `1px solid hsl(${c.border})` }} />
                  ))}
                </div>
                {/* Player: cover · play · waveform · chat */}
                <div className={cn("mt-auto h-3 flex items-center gap-0.5", menuSide === "left" && "flex-row-reverse")} style={{ background: `hsl(${c.card})` }}>
                  <div className="w-3 h-3" style={{ background: accent }} />
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: primary }} />
                  <div className="flex-1 h-0.5 rounded" style={{ background: primary, opacity: 0.7 }} />
                  <div className="w-3 h-3" style={{ border: `1px solid ${fg}`, opacity: 0.8 }} />
                </div>
              </div>
              {/* The menu column: menu button on top, then icons */}
              <div className="w-3.5 h-full flex flex-col items-center gap-1 py-0.5" style={{ background: sidebar }}>
                <div className="w-2.5 h-2.5 rounded-[1px]" style={{ border: `1px solid hsl(${c.sidebarForeground})` }} />
                <div className="w-2.5 h-2" style={{ background: primary }} />
                {[0.6, 0.6, 0.6, 0.6].map((op, i) => (
                  <div key={i} className="w-1.5 h-1.5 rounded-sm" style={{ background: `hsl(${c.sidebarForeground})`, opacity: op }} />
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-1 px-0.5">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate text-foreground">{preset.name}</p>
              </div>
              <div className="flex items-center gap-0.5">
                {[bg, primary, accent].map((col, i) => (
                  <div key={i} className="w-2.5 h-2.5 rounded-full border border-black/10" style={{ background: col }} />
                ))}
              </div>
            </div>

            {isActive && (
              <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow">
                {isLoading
                  ? <Loader2 className="w-3 h-3 text-primary-foreground animate-spin" />
                  : <Check className="w-3 h-3 text-primary-foreground" />}
              </div>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px]">
          <p className="text-xs font-medium">{preset.name}</p>
          <p className="text-xs text-muted-foreground">{preset.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Section label helpers ────────────────────────────────────────────────
const SECTION_LABELS: Record<GifSection, string> = {
  body:    'Body',
  header:  'Header',
  sidebar: 'Menu',
  card:    'Cards',
  player:  'Player',
};
// Monochrome category icons instead of colour emoji (Gordon 2026-11-02:
// "change all colored icons in theme palette buttons, monochrome").
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  system: Monitor, accessibility: Accessibility, bb: Wheat, music: Music, design: Palette,
  nature: Leaf, sport: Trophy, country: Globe, platform: Headphones,
};
function CategoryIcon({ id, className }: { id: string; className?: string }) {
  const Icon = CATEGORY_ICONS[id];
  return Icon ? <Icon className={cn("inline-block h-3.5 w-3.5 shrink-0", className)} aria-hidden="true" /> : null;
}

// A picture that may be gone (an expired GIF link): when it fails it shows the
// section's theme colour instead of the browser's broken-image symbol
// (Gordon 2026-11-02: "menu background there missing image symbol, it should
// be the theme color").
const SECTION_SWATCH: Record<GifSection, string> = {
  body: 'bg-background', header: 'bg-background', sidebar: 'bg-sidebar', card: 'bg-card', player: 'bg-background',
};
function SafeImg({ src, alt, className, style, swatch = 'bg-muted' }: { src?: string; alt?: string; className?: string; style?: React.CSSProperties; swatch?: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  if (!src || broken) return <span aria-hidden="true" className={cn('block border border-border/40', className, swatch)} />;
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt={alt || ''} className={className} style={style} loading="lazy" onError={() => setBroken(true)} />;
}

// ─── Small GIF thumbnail with overlay actions ──────────────────────────────
function GifThumbnail({
  gif,
  onRemove,
  onAssign,
  assignLabel,
}: {
  gif: GifEntry;
  onRemove?: () => void;
  onAssign?: () => void;
  assignLabel?: string;
}) {
  return (
    <div
      className="relative group rounded-lg overflow-hidden border border-border/50 cursor-grab active:cursor-grabbing"
      draggable
      onDragStart={e => {
        e.dataTransfer.setData('text/bb-gif-url', gif.url);
        e.dataTransfer.setData('text/plain', gif.url);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      title="Drag onto a section row above to pin"
    >
      <SafeImg src={gif.url} alt={gif.label || 'GIF'} className="w-full aspect-video object-cover pointer-events-none" />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors" />
      <div className="absolute inset-0 hidden group-hover:flex touch:!flex flex-col items-center justify-center gap-1 p-1">
        {onAssign && (
          <button
            onClick={onAssign}
            className="text-[9px] font-semibold bg-primary text-primary-foreground rounded px-1.5 py-0.5 truncate max-w-full"
          >
            {assignLabel || 'Assign'}
          </button>
        )}
        {onRemove && (
          <button
            onClick={onRemove}
            className="w-5 h-5 bg-destructive rounded-full flex items-center justify-center"
          >
            <Trash2 className="h-2.5 w-2.5 text-white" />
          </button>
        )}
      </div>
      {gif.label && (
        <p className="absolute bottom-0 left-0 right-0 text-[10px] text-white bg-black/50 px-1.5 py-0.5 truncate">
          {gif.label}
        </p>
      )}
    </div>
  );
}

// ─── GIF Theme Panel ──────────────────────────────────────────────────────
interface GifThemePanelProps {
  enabled: boolean;
  library: GifEntry[];
  sectionGifs: Record<string, string>;
  onToggle: (enabled: boolean) => void;
  onUpdateLibrary: (gifs: GifEntry[]) => void;
  onSetSectionGif: (section: string, url: string | null) => void;
  onClearSectionGifs: () => void;
  onRandomize: () => void;
  userId?: string;
  isSuperAdmin?: boolean;
  /** Firestore-managed GIF skins loaded by theme context */
  gifSkins?: GifSkin[];
  // Skin props
  activeSkinId?: string | null;
  onApplySkin?: (id: string) => void;
  onClearSkin?: () => void;
  // Visual controls
  gifOpacity?: number;
  gifBgColor?: string;
  gifTextColor?: string;
  gifBold?: boolean;
  onSetGifOpacity?: (v: number) => void;
  onSetGifBgColor?: (v: string) => void;
  onSetGifTextColor?: (v: string) => void;
  onSetGifBold?: (v: boolean) => void;
  gifSize?: string;
  onSetGifSize?: (v: GifSizeMode) => void;
  gifTileSize?: number;
  onSetGifTileSize?: (v: number) => void;
}

function GifThemePanel(props: GifThemePanelProps) {
  const { bgFxSound, setBgFxSound, bgFxTilt, setBgFxTilt } = useTheme();
  return <GifThemePanelInner {...props} bgFx={{ bgFxSound, setBgFxSound, bgFxTilt, setBgFxTilt }} />;
}

function GifThemePanelInner({
  enabled, library, sectionGifs,
  onToggle, onUpdateLibrary, onSetSectionGif, onClearSectionGifs, onRandomize,
  userId, isSuperAdmin,
  gifSkins = [],
  activeSkinId, onApplySkin, onClearSkin,
  gifOpacity = 0.6, gifBgColor = '', gifTextColor = '', gifBold = false,
  gifSize = 'cover', gifTileSize = 200,
  onSetGifOpacity, onSetGifBgColor, onSetGifTextColor, onSetGifBold, onSetGifSize, onSetGifTileSize,
  bgFx,
}: GifThemePanelProps & { bgFx?: { bgFxSound: boolean; setBgFxSound: (v: boolean) => void; bgFxTilt: boolean; setBgFxTilt: (v: boolean) => void } }) {
  const [gifTab, setGifTab] = useState<'skins' | 'my' | 'vectors' | 'search' | 'packs'>('skins');
  const [skinCategory, setSkinCategory] = useState<string>('all');

  // ── My GIFs sub-state ─────────────────────────────────────────────────
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Grid columns for the library thumbnails (1–5)
  const [libCols, setLibCols] = useState(5);
  // Which section we're assigning from the library
  const [assigningSection, setAssigningSection] = useState<GifSection | null>(null);

  // ── Search sub-state ──────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GifEntry[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  // Which section the search result will be assigned to
  const [searchTargetSection, setSearchTargetSection] = useState<GifSection>('body');

  // ── Packs sub-state ───────────────────────────────────────────────────
  const [packs, setPacks] = useState<GifThemePack[]>([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [newPackName, setNewPackName] = useState('');
  const [newPackDesc, setNewPackDesc] = useState('');
  const [savingPack, setSavingPack] = useState(false);

  // Load packs when switching to packs tab
  useEffect(() => {
    if (gifTab !== 'packs') return;
    setPacksLoading(true);
    const loader = isSuperAdmin ? getAllGifPacks() : getPublicGifPacks();
    loader.then(setPacks).catch(() => setPacks([])).finally(() => setPacksLoading(false));
  }, [gifTab, isSuperAdmin]);

  // ── My GIFs helpers ───────────────────────────────────────────────────
  const addUrl = () => {
    if (!newUrl.trim()) return;
    onUpdateLibrary([...library, {
      id: Date.now().toString(),
      url: newUrl.trim(),
      label: newLabel.trim() || undefined,
    }]);
    setNewUrl(''); setNewLabel('');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    const newEntries: GifEntry[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const path = userId
          ? getStoragePath('theme-image', userId, `gif-${Date.now()}-${file.name}`)
          : `admin-uploads/gif-library/${Date.now()}-${file.name}`;
        const url = await uploadFile(file, path);
        newEntries.push({ id: `${Date.now()}-${i}`, url, label: file.name.replace(/\.[^.]+$/, '') });
      } catch (err) { console.error('GIF upload failed:', err); }
      setUploadProgress({ done: i + 1, total: files.length });
    }
    if (newEntries.length) onUpdateLibrary([...library, ...newEntries]);
    setUploading(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Search helpers ────────────────────────────────────────────────────
  const runSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true); setSearchError('');
    try {
      const res = await fetch(`/api/gifs/search?q=${encodeURIComponent(searchQuery)}&limit=16`);
      const json = await res.json();
      setSearchResults((json.results ?? []).map((r: { id: string; title: string; url: string; preview?: string }) => ({
        id: r.id, url: r.url, label: r.title,
      })));
      if (!json.results?.length) setSearchError('No results found');
    } catch { setSearchError('Search failed'); }
    finally { setSearching(false); }
  };

  const assignSearchResult = (gif: GifEntry) => {
    // Pin this GIF to the target section
    onSetSectionGif(searchTargetSection, gif.url);
    // Also add to personal library if not already there
    if (!library.some(g => g.id === gif.id || g.url === gif.url)) {
      onUpdateLibrary([...library, gif]);
    }
  };

  // ── Pack helpers ──────────────────────────────────────────────────────
  const applyPack = (pack: GifThemePack) => {
    // Merge pack GIFs into personal library (no duplicates)
    const existing = new Set(library.map(g => g.url));
    const toAdd = pack.gifs.filter(g => !existing.has(g.url));
    if (toAdd.length > 0) onUpdateLibrary([...library, ...toAdd]);
    if (!enabled) onToggle(true);
  };

  const handleSavePack = async () => {
    if (!newPackName.trim() || library.length === 0 || !userId) return;
    setSavingPack(true);
    try {
      await saveGifPack({
        name: newPackName.trim(),
        description: newPackDesc.trim(),
        gifs: library,
        createdBy: userId,
        isPublic: true,
      });
      setNewPackName(''); setNewPackDesc('');
      // Reload packs
      const updated = await getAllGifPacks();
      setPacks(updated);
    } catch (err) { console.error('Save pack failed:', err); }
    finally { setSavingPack(false); }
  };

  const handleDeletePack = async (id: string) => {
    if (!confirm('Delete this pack?')) return;
    await deleteGifPack(id);
    setPacks(prev => prev.filter(p => p.id !== id));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Hidden file input — outside ScrollArea to avoid browser click-security blocks; multiple allows batch selection */}
      <input ref={fileInputRef} type="file" accept="image/gif,image/*" multiple className="hidden" onChange={handleFileUpload} />

      {/* Enable toggle */}
      <div className="px-4 pt-3 pb-2 flex-shrink-0">
        <div className="flex items-center justify-between p-2.5 rounded-lg border border-border/50 bg-muted/30">
          <div>
            <p className="text-xs font-semibold">Image &amp; Video Backgrounds</p>
            <p className="text-[10px] text-muted-foreground">Rotates every 20 s · per-section control below</p>
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onRandomize} disabled={library.length === 0}>
                    <Shuffle className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Randomize all sections now</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Switch checked={enabled} onCheckedChange={onToggle} />
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="px-4 pb-1 flex-shrink-0">
        <div className="flex gap-1 bg-muted/40 rounded-lg p-0.5">
          {([
            { id: 'skins',  label: 'Skins',     icon: <Sparkles className="h-3 w-3" /> },
            { id: 'my',     label: 'My GIFs',   icon: <Film className="h-3 w-3" /> },
            { id: 'vectors', label: 'Vectors',  icon: <Triangle className="h-3 w-3" /> },
            { id: 'search', label: 'Search',    icon: <Globe className="h-3 w-3" /> },
            { id: 'packs',  label: 'Community', icon: <Users className="h-3 w-3" /> },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => setGifTab(tab.id)}
              className={cn(
                'flex-1 flex items-center justify-center gap-1 text-[10px] font-medium py-1 rounded-md transition-colors',
                gifTab === tab.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── SKINS tab ────────────────────────────────────────────────── */}
      {gifTab === 'skins' && (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Category filter row — derived dynamically from loaded skins */}
          <div className="px-3 pb-2 flex-shrink-0">
            <div className="flex gap-1 overflow-x-auto hide-scrollbar pb-1">
              {(['all', ...Array.from(new Set(gifSkins.map(s => s.category).filter(Boolean)))]).map(cat => (
                <button
                  key={cat}
                  onClick={() => setSkinCategory(cat)}
                  className={cn(
                    'flex-shrink-0 text-[9px] font-medium px-2 py-0.5 rounded-full border transition-colors',
                    skinCategory === cat
                      ? 'bg-primary text-white border-primary'
                      : 'border-border/50 text-muted-foreground hover:text-foreground'
                  )}
                >
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>
          </div>

          {/* Active skin banner */}
          {activeSkinId && (
            <div className="mx-3 mb-2 flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-primary/15 border border-primary/30 flex-shrink-0">
              <span className="text-[10px] font-medium text-primary">
                ✦ {gifSkins.find(s => s.id === activeSkinId)?.name ?? activeSkinId}
              </span>
              <button
                onClick={onClearSkin}
                className="text-[9px] text-primary/70 hover:text-primary underline"
              >
                Clear
              </button>
            </div>
          )}

          {/* ── GIF Visual Controls ─────────────────────────────────── */}
          <div className="mx-3 mb-2 flex-shrink-0 rounded-lg border border-border/40 bg-muted/30 p-2.5 space-y-2.5">
            <p className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">GIF Appearance</p>

            {/* Opacity slider */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-[10px] text-foreground">Opacity</label>
                <span className="text-[10px] text-muted-foreground">{Math.round(gifOpacity * 100)}%</span>
              </div>
              <input
                type="range" min={0.05} max={1} step={0.01}
                value={gifOpacity}
                onChange={e => onSetGifOpacity?.(parseFloat(e.target.value))}
                className="w-full h-1.5 accent-primary rounded-full"
              />
            </div>

            {/* Background colour */}
            <div className="flex items-center justify-between gap-2">
              <label className="text-[10px] text-foreground shrink-0">BG Colour</label>
              <div className="flex items-center gap-1.5 ml-auto">
                <input
                  type="color"
                  value={gifBgColor || '#000000'}
                  onChange={e => onSetGifBgColor?.(e.target.value)}
                  className="h-5 w-8 rounded cursor-pointer border border-border/40 bg-transparent p-0"
                  title="Background colour behind GIF"
                />
                {gifBgColor && (
                  <button
                    onClick={() => onSetGifBgColor?.('')}
                    className="text-[9px] text-muted-foreground hover:text-foreground underline"
                  >Clear</button>
                )}
              </div>
            </div>

            {/* Text colour */}
            <div className="flex items-center justify-between gap-2">
              <label className="text-[10px] text-foreground shrink-0">Text Colour</label>
              <div className="flex items-center gap-1.5 ml-auto">
                <input
                  type="color"
                  value={gifTextColor || '#ffffff'}
                  onChange={e => onSetGifTextColor?.(e.target.value)}
                  className="h-5 w-8 rounded cursor-pointer border border-border/40 bg-transparent p-0"
                  title="Override text colour over GIF"
                />
                {gifTextColor && (
                  <button
                    onClick={() => onSetGifTextColor?.('')}
                    className="text-[9px] text-muted-foreground hover:text-foreground underline"
                  >Clear</button>
                )}
              </div>
            </div>

            {/* Bold toggle */}
            <div className="flex items-center justify-between">
              <label className="text-[10px] text-foreground">Bold text</label>
              <button
                onClick={() => onSetGifBold?.(!gifBold)}
                className={cn(
                  'relative h-4 w-7 rounded-full transition-colors',
                  gifBold ? 'bg-primary' : 'bg-muted-foreground/30'
                )}
              >
                <span className={cn(
                  'absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all',
                  gifBold ? 'left-[14px]' : 'left-0.5'
                )} />
              </button>
            </div>

            {/* Background FX — sound pulse + tilt parallax */}
            {bgFx && ([
              ['React to sound', bgFx.bgFxSound, () => bgFx.setBgFxSound(!bgFx.bgFxSound)],
              ['React to tilt (mobile)', bgFx.bgFxTilt, () => bgFx.setBgFxTilt(!bgFx.bgFxTilt)],
            ] as [string, boolean, () => void][]).map(([label, on, toggle]) => (
              <div key={label} className="flex items-center justify-between">
                <label className="text-[10px] text-foreground">{label}</label>
                <button
                  onClick={toggle}
                  className={cn('relative h-4 w-7 rounded-full transition-colors', on ? 'bg-primary' : 'bg-muted-foreground/30')}
                >
                  <span className={cn('absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all', on ? 'left-[14px]' : 'left-0.5')} />
                </button>
              </div>
            ))}

            {/* Size / tiling mode */}
            <div className="space-y-1">
              <label className="text-[10px] text-foreground">Size / Tiling</label>
              <div className="grid grid-cols-5 gap-1">
                {(['cover', 'fill', 'contain', 'tile', 'original'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => onSetGifSize?.(mode)}
                    className={cn(
                      'px-1 py-1 rounded text-[9px] font-medium capitalize border transition-colors',
                      gifSize === mode
                        ? 'bg-primary text-white border-primary'
                        : 'border-border/50 text-muted-foreground hover:text-foreground hover:border-border'
                    )}
                  >{mode}</button>
                ))}
              </div>
              {/* Tile size — only meaningful in tile mode */}
              {gifSize === 'tile' && (
                <div className="pt-1.5 space-y-1">
                  <div className="flex justify-between text-[9px] text-muted-foreground">
                    <span>Tile size</span><span>{gifTileSize}px</span>
                  </div>
                  <input
                    type="range" min={16} max={600} step={4}
                    value={gifTileSize}
                    onChange={e => onSetGifTileSize?.(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Skins grid */}
          <ScrollArea className="flex-1">
            <div className="px-3 pb-3 grid gap-2"
              style={{ gridTemplateColumns: `repeat(${libCols}, minmax(0, 1fr))` }}>
              {gifSkins.length === 0 ? (
                <div className="col-span-full py-8 text-center">
                  <p className="text-[11px] text-muted-foreground">No image/video skins yet.</p>
                  <p className="text-[10px] text-muted-foreground/60 mt-1">Super Admin can add skins in the Image Skins tab.</p>
                </div>
              ) : gifSkins
                .filter(s => skinCategory === 'all' || s.category === skinCategory)
                .map(skin => {
                  const isActive = activeSkinId === skin.id;
                  return (
                    <div key={skin.id} className="relative group">
                      <button
                        onClick={() => isActive ? onClearSkin?.() : onApplySkin?.(skin.id)}
                        className={cn(
                          'relative w-full rounded-lg overflow-hidden border-2 transition-all text-left',
                          isActive
                            ? 'border-primary shadow-md shadow-primary/30'
                            : 'border-border/40 hover:border-primary/60'
                        )}
                        style={{ aspectRatio: '16/9' }}
                        title={isActive ? 'Click to remove skin' : skin.name}
                      >
                        {/* GIF preview */}
                        <SafeImg
                          src={skin.bodyUrl}
                          alt={skin.name}
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{ opacity: skin.bodyOpacity ?? 0.72 }}
                        />
                        {/* Dark scrim for text */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        {/* Labels */}
                        <div className="absolute bottom-0 left-0 right-0 px-1.5 pb-1.5">
                          <p className="text-[9px] font-semibold text-white leading-tight truncate">{skin.name}</p>
                          <p className="text-[8px] text-white/60 uppercase tracking-wide">{skin.category}</p>
                        </div>
                        {/* Active badge / hover remove indicator */}
                        {isActive ? (
                          <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary group-hover:bg-destructive flex items-center justify-center transition-colors">
                            <span className="text-[8px] text-white group-hover:hidden">✓</span>
                            <span className="text-[8px] text-white hidden group-hover:block touch:!block">✕</span>
                          </div>
                        ) : (
                          <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-black/60 bb-hover-reveal flex items-center justify-center transition-opacity">
                            <span className="text-[8px] text-white">+</span>
                          </div>
                        )}
                      </button>
                    </div>
                  );
                })}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* ── MY GIFs tab ──────────────────────────────────────────────── */}
      {gifTab === 'my' && (
        <ScrollArea className="flex-1">
          <div className="px-4 pb-8 space-y-4">

            {/* Per-section assignment */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Section Assignment</p>
                {Object.keys(sectionGifs).length > 0 && (
                  <button onClick={onClearSectionGifs} className="text-[9px] text-destructive hover:underline">
                    Clear all pins
                  </button>
                )}
              </div>
              {GIF_SECTIONS.map(section => {
                const pinnedUrl = sectionGifs[section];
                const pinnedGif = library.find(g => g.url === pinnedUrl);
                return (
                  <div
                    key={section}
                    className="flex items-center gap-2 p-1.5 rounded-lg bg-muted/20 border border-border/30 transition-colors data-[dragover=true]:border-primary data-[dragover=true]:bg-primary/10"
                    onDragOver={e => { e.preventDefault(); e.currentTarget.setAttribute('data-dragover', 'true'); }}
                    onDragLeave={e => e.currentTarget.setAttribute('data-dragover', 'false')}
                    onDrop={e => {
                      e.preventDefault();
                      e.currentTarget.setAttribute('data-dragover', 'false');
                      const url = e.dataTransfer.getData('text/bb-gif-url') || e.dataTransfer.getData('text/plain');
                      if (url) onSetSectionGif(section, url);
                    }}
                  >
                    <span className="text-[10px] w-16 flex-shrink-0 text-muted-foreground">
                      {SECTION_LABELS[section]}
                    </span>
                    {pinnedUrl ? (
                      <>
                        <SafeImg src={pinnedUrl} className="h-7 w-12 object-cover rounded flex-shrink-0" swatch={SECTION_SWATCH[section]} />
                        <span className="text-[9px] flex-1 truncate text-muted-foreground">
                          {pinnedGif?.label || 'Pinned'}
                        </span>
                        {/* Change the picture (Gordon 2026-11-02: "change image in custom image bg"). */}
                        {library.length > 0 && (
                          <button
                            onClick={() => setAssigningSection(section === assigningSection ? null : section)}
                            className={cn(
                              'text-[9px] px-1.5 py-0.5 rounded border flex-shrink-0 transition-colors',
                              assigningSection === section ? 'bg-primary text-primary-foreground border-primary' : 'border-border/50 hover:border-border'
                            )}
                          >
                            Change <ChevronDown className="h-2.5 w-2.5 inline" />
                          </button>
                        )}
                        <button onClick={() => onSetSectionGif(section, null)} aria-label="Remove" className="text-[9px] text-destructive flex-shrink-0">✕</button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-[9px] text-muted-foreground/60 italic">Random from library</span>
                        {library.length > 0 && (
                          <button
                            onClick={() => setAssigningSection(section === assigningSection ? null : section)}
                            className={cn(
                              'text-[9px] px-1.5 py-0.5 rounded border flex-shrink-0 transition-colors',
                              assigningSection === section ? 'bg-primary text-primary-foreground border-primary' : 'border-border/50 hover:border-border'
                            )}
                          >
                            Pin <ChevronDown className="h-2.5 w-2.5 inline" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              {/* Library picker for selected section */}
              {assigningSection && library.length > 0 && (
                <div className="rounded-lg border border-primary/40 bg-muted/20 p-2 space-y-1.5">
                  <p className="text-[9px] text-muted-foreground">Pick a GIF to pin to <strong>{SECTION_LABELS[assigningSection]}</strong>:</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {library.map(gif => (
                      <button
                        key={gif.id}
                        onClick={() => { onSetSectionGif(assigningSection, gif.url); setAssigningSection(null); }}
                        className="relative rounded overflow-hidden aspect-video hover:ring-2 ring-primary"
                      >
                        <SafeImg src={gif.url} alt={gif.label || ''} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Add by URL */}
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Add GIF by URL</p>
              <div className="flex gap-1.5">
                <Input value={newUrl} onChange={e => setNewUrl(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addUrl()}
                  placeholder="https://… .gif"
                  className="flex-1 h-7 text-xs !text-foreground !bg-background placeholder:text-muted-foreground" />
                <Button size="sm" className="h-7 px-2" onClick={addUrl} disabled={!newUrl.trim()}>
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
              <Input value={newLabel} onChange={e => setNewLabel(e.target.value)}
                placeholder="Label (optional)"
                className="h-7 text-xs !text-foreground !bg-background placeholder:text-muted-foreground" />
            </div>

            {/* Upload — supports selecting multiple files at once */}
            <div>
              <Button variant="outline" size="sm" className="w-full h-7 text-xs"
                onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading && uploadProgress
                  ? <><RefreshCw className="h-3 w-3 mr-1.5 animate-spin" />Uploading {uploadProgress.done}/{uploadProgress.total}…</>
                  : uploading
                  ? <><RefreshCw className="h-3 w-3 mr-1.5 animate-spin" />Uploading…</>
                  : <><Plus className="h-3 w-3 mr-1.5" />Upload GIFs / Images</>}
              </Button>
              {!uploading && (
                <p className="text-[9px] text-muted-foreground text-center mt-0.5">Hold ⌘/Ctrl to select multiple files</p>
              )}
            </div>

            {/* Library grid */}
            {library.length > 0 ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
                    Library ({library.length})
                  </p>
                  {/* Per-row column slider: 1–9 thumbnails */}
                  <div className="flex items-center gap-1.5 flex-1 justify-center">
                    <span className="text-[9px] text-muted-foreground shrink-0">1</span>
                    <input
                      type="range" min={1} max={9} step={1} value={libCols}
                      onChange={e => setLibCols(Number(e.target.value))}
                      className="flex-1 h-1 accent-primary cursor-pointer"
                      title={`${libCols} per row`}
                    />
                    <span className="text-[9px] text-muted-foreground shrink-0">9</span>
                    <span className="text-[9px] font-medium text-foreground/60 w-5 text-right shrink-0">{libCols}×</span>
                  </div>
                  <button className="text-[9px] text-destructive hover:underline shrink-0"
                    onClick={() => { if (confirm('Clear entire GIF library?')) { onUpdateLibrary([]); onClearSectionGifs(); } }}>
                    Clear all
                  </button>
                </div>
                <div
                  className="grid gap-2"
                  style={{ gridTemplateColumns: `repeat(${libCols}, minmax(0, 1fr))` }}
                >
                  {library.map(gif => (
                    <GifThumbnail
                      key={gif.id}
                      gif={gif}
                      onRemove={() => onUpdateLibrary(library.filter(g => g.id !== gif.id))}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Film className="h-7 w-7 mx-auto mb-1.5 opacity-30" />
                <p className="text-xs">No GIFs yet</p>
                <p className="text-[10px] opacity-60">Add URLs, upload, or search online</p>
              </div>
            )}
          </div>
        </ScrollArea>
      )}

      {/* ── VECTORS tab ──────────────────────────────────────────────── */}
      {gifTab === 'vectors' && (
        <ScrollArea className="flex-1">
          <VectorPatternGenerator
            onAdd={(entry) => onUpdateLibrary([...library, entry])}
            sections={GIF_SECTIONS}
            sectionLabels={SECTION_LABELS}
            onPinToSection={(section, url) => {
              onUpdateLibrary([...library, { id: `vec-${Date.now()}`, url, label: 'Vector pattern' }]);
              onSetSectionGif(section as GifSection, url);
            }}
          />
        </ScrollArea>
      )}

      {/* ── SEARCH tab ───────────────────────────────────────────────── */}
      {gifTab === 'search' && (
        <div className="flex flex-col flex-1 min-h-0 px-4 gap-3">
          {/* Section selector */}
          <div className="flex-shrink-0 pt-1">
            <p className="text-[10px] text-muted-foreground mb-1">Assign search result to:</p>
            <div className="flex flex-wrap gap-1">
              {GIF_SECTIONS.map(s => (
                <button
                  key={s}
                  onClick={() => setSearchTargetSection(s)}
                  className={cn(
                    'text-[9px] px-2 py-0.5 rounded-full border transition-colors',
                    searchTargetSection === s
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border/50 hover:border-border'
                  )}
                >
                  {SECTION_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Search bar */}
          <div className="flex gap-1.5 flex-shrink-0">
            <Input type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runSearch()}
              placeholder="Search Giphy (e.g. 'neon city')"
              className="flex-1 h-8 text-xs !text-foreground !bg-background placeholder:text-muted-foreground"
            />
            <Button size="sm" className="h-8 px-2" onClick={runSearch} disabled={searching || !searchQuery.trim()}>
              {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            </Button>
          </div>

          {searchError && <p className="text-[10px] text-destructive flex-shrink-0">{searchError}</p>}

          {/* Results grid */}
          <ScrollArea className="flex-1">
            <div className="pb-8">
              {searchResults.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {searchResults.map(gif => (
                    <GifThumbnail
                      key={gif.id}
                      gif={gif}
                      onAssign={() => assignSearchResult(gif)}
                      assignLabel={`→ ${SECTION_LABELS[searchTargetSection].replace(/^[^ ]+ /, '')}`}
                    />
                  ))}
                </div>
              ) : (
                !searching && (
                  <div className="text-center py-10 text-muted-foreground">
                    <Globe className="h-7 w-7 mx-auto mb-2 opacity-30" />
                    <p className="text-xs">Search to find GIFs online</p>
                    <p className="text-[10px] opacity-60">Powered by Giphy</p>
                  </div>
                )
              )}
              {searching && (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* ── COMMUNITY tab ─────────────────────────────────────────────── */}
      {gifTab === 'packs' && (
        <ScrollArea className="flex-1">
          <div className="px-4 pb-8 space-y-4 pt-3">
            {/* Community skins grid — shows gifSkins with shared:true */}
            {gifSkins.filter(s => (s as unknown as Record<string, unknown>).shared).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-7 w-7 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No community skins shared yet</p>
                <p className="text-[10px] opacity-60 mt-1">Be the first — share your active skin below!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {gifSkins.filter(s => (s as unknown as Record<string, unknown>).shared).map(skin => {
                  const thumbUrl = (() => {
                    const url = skin.bodyUrl || '';
                    if (!url) return '';
                    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|shorts\/))([A-Za-z0-9_-]{11})/);
                    if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
                    if (/\.(mp4|webm|ogg|mov)(\?|$)/i.test(url)) return '';
                    if (/player\.vimeo\.com|vimeo\.com/i.test(url)) return '';
                    return url;
                  })();
                  return (
                    <div key={skin.id} className="relative group rounded-lg overflow-hidden border border-border/40 bg-muted/30 aspect-video cursor-pointer"
                      onClick={() => onApplySkin?.(skin.id)}>
                      {thumbUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={thumbUrl} alt={skin.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted/60">
                          <Film className="h-5 w-5 opacity-40" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                        <p className="text-white text-[10px] font-semibold text-center px-1 truncate w-full text-center">{skin.name}</p>
                        <span className="text-[9px] text-white/70">Tap to apply</span>
                      </div>
                      {activeSkinId === skin.id && (
                        <div className="absolute top-1 right-1 bg-primary rounded-full p-0.5">
                          <Check className="h-2.5 w-2.5 text-primary-foreground" />
                        </div>
                      )}
                      {isSuperAdmin && (
                        <button
                          className="absolute top-1 left-1 text-destructive bb-hover-reveal transition-opacity bg-background/80 rounded-full p-0.5"
                          onClick={e => { e.stopPropagation(); handleDeletePack(skin.id); }}>
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Share active skin to community */}
            {activeSkinId && (
              <div className="border border-dashed border-border/60 rounded-lg p-3 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Share2 className="h-3 w-3" /> Share Active Skin
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Share &ldquo;{gifSkins.find(s => s.id === activeSkinId)?.name ?? 'this skin'}&rdquo; with the community so others can use it.
                </p>
                <Button size="sm" className="w-full h-7 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={savingPack}
                  onClick={async () => {
                    if (!activeSkinId) return;
                    setSavingPack(true);
                    try {
                      const { updateGifSkin } = await import('@/services/gif-skins');
                      await updateGifSkin(activeSkinId, { shared: true } as unknown as Record<string, unknown>);
                    } finally { setSavingPack(false); }
                  }}>
                  {savingPack ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Sharing…</> : <><Share2 className="h-3 w-3 mr-1.5" />Share to Community</>}
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

// ─── Main ThemePicker floating panel ──────────────────────────────────────
interface ThemePickerProps {
  open: boolean;
  onClose: () => void;
  /** True when the sidebar is expanded on desktop — shifts the panel right */
  sidebarExpanded?: boolean;
  /** True on mobile — panel goes full-width with no sidebar offsets */
  isMobile?: boolean;
}

/** The docked theme panel's width — the page narrows by the same amount. */
const THEME_PANEL_W = "min(440px, 38vw)";

export function ThemePicker({ open, onClose, sidebarExpanded = false, isMobile = false }: ThemePickerProps) {
  const menuSide = useMenuSide();
  const tCommon = useTranslations("common");
  const {
    currentTheme, setUserTheme,
    colorRotationEnabled, setColorRotationEnabled, rotateOneStep, applyRandomColorTheme,
    gifThemeEnabled, gifLibrary, sectionGifs,
    setGifThemeEnabled, updateGifLibrary, setSectionGif, clearSectionGifs, randomizeGifSections,
    activeSkinId, applyGifSkin, clearGifSkin, applyRandomBackdrop, bgDemo, setBgDemo,
    gifSkins,
    gifOpacity, gifBgColor, gifTextColor, gifBold,
    setGifOpacity, setGifBgColor, setGifTextColor, setGifBold,
    gifSize, setGifSize, gifTileSize, setGifTileSize,
    setAppearanceMode: setContextAppearanceMode,
    appearanceMode: contextAppearanceMode,
  } = useTheme();

  const tSettings = useTranslations("settings");
  // Tracks how many times the random button has been pressed.
  // Odd press → random colour theme. Even press → a random background from the
  // mixed pool: Image Skins, GIFs, release artwork, BB videos (Gordon 2026-11-02).
  const randomPressRef = useRef(0);
  const [randomIsGif, setRandomIsGif] = useState(false); // visual hint for next press

  const handleRandomTheme = useCallback(() => {
    randomPressRef.current += 1;
    const isGifTurn = randomPressRef.current % 2 === 0;
    setRandomIsGif(randomPressRef.current % 2 !== 0); // next press will be opposite

    if (isGifTurn) {
      void applyRandomBackdrop();
      return;
    }
    // Random colour theme
    applyRandomColorTheme();
  }, [applyRandomBackdrop, applyRandomColorTheme]);
  const { user, userProfile } = useAuth();
  const isSuperAdmin = userProfile?.accountType === 'superadmin' ||
    (user?.email === 'breakinbreadswe@gmail.com');
  const [activeCategory, setActiveCategory] = useState<ThemeCategory | "all" | "custom" | "gif">("all");
  const [search, setSearch] = useState("");
  const [applying, setApplying] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Dark / Light / System toggle — driven by ThemeContext for reliable cross-device updates.
  // Local state stays in sync with context so the UI (selected button) is always correct.
  const themeMode = contextAppearanceMode;

  const handleThemeMode = (mode: 'dark' | 'light' | 'system') => {
    // Delegate fully to context — it updates localStorage, dark class, CSS vars,
    // and React state all synchronously.  This is the reliable path on mobile.
    setContextAppearanceMode(mode);
  };

  // Panel position
  const [side, setSide] = useState<PanelSide>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem(LS_SIDE_KEY) as PanelSide) || "right";
    }
    return "right";
  });

  // Font scale (1.0 = 100% = default 16px)
  const [fontScale, setFontScale] = useState<number>(() => {
    if (typeof window !== "undefined") {
      const stored = parseFloat(localStorage.getItem(LS_FONT_KEY) || "1");
      return isNaN(stored) ? 1 : stored;
    }
    return 1;
  });

  // Apply saved font scale on mount
  useEffect(() => {
    applyFontScale(fontScale);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFontScale = (val: number) => {
    setFontScale(val);
    applyFontScale(val);
    localStorage.setItem(LS_FONT_KEY, String(val));
  };

  // Contrast vector overlay — toggled here; ContrastVectorMount listens for the
  // `bb:set-vector-overlay` event and persists the choice to localStorage.
  const [vectorOn, setVectorOn] = useState(true);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("bb-vector-overlay");
      setVectorOn(raw ? JSON.parse(raw).enabled !== false : true);
    } catch { setVectorOn(true); }
  }, []);
  const handleVectorToggle = (on: boolean) => {
    setVectorOn(on);
    try {
      const raw = localStorage.getItem("bb-vector-overlay");
      const prev = raw ? JSON.parse(raw) : {};
      localStorage.setItem("bb-vector-overlay", JSON.stringify({ ...prev, enabled: on }));
    } catch { /* ignore */ }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("bb:set-vector-overlay", { detail: { enabled: on } }));
    }
  };

  // Button shape (stored in localStorage)
  const [btnRadius, setBtnRadius] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(LS_BTN_RADIUS_KEY) || "6px";
    }
    return "6px";
  });

  useEffect(() => {
    applyBtnRadius(btnRadius);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBtnRadius = (value: string) => {
    setBtnRadius(value);
    applyBtnRadius(value);
    localStorage.setItem(LS_BTN_RADIUS_KEY, value);
  };

  const handleSide = (s: PanelSide) => {
    setSide(s);
    localStorage.setItem(LS_SIDE_KEY, s);
  };

  const filteredPresets = useMemo(() => {
    let list = themePresets;
    if (activeCategory !== "all" && activeCategory !== "custom" && activeCategory !== "gif") {
      list = list.filter((p) => p.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeCategory, search]);

  const handleSelect = useCallback(
    async (preset: (typeof themePresets)[number]) => {
      // Clicking the already-active theme triggers a manual color rotation step
      if (currentTheme?.id === preset.id) {
        rotateOneStep();
        return;
      }
      setApplying(preset.id);
      try {
        await setUserTheme(preset.id);
      } catch {
        // silent
      } finally {
        setApplying(null);
      }
    },
    [currentTheme, rotateOneStep, setUserTheme]
  );

  const handleExport = () => {
    if (!currentTheme) return;
    const blob = new Blob([JSON.stringify(currentTheme, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bb-theme-${currentTheme.name?.toLowerCase().replace(/\s+/g, "-") ?? "custom"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open]);

  // While docked left/right on desktop, the page narrows by the panel's width.
  useEffect(() => {
    const root = document.documentElement;
    if (open && !isMobile && (side === "left" || side === "right")) {
      root.setAttribute("data-theme-panel", side);
      root.style.setProperty("--bb-theme-panel-w", THEME_PANEL_W);
    } else {
      root.removeAttribute("data-theme-panel");
    }
    return () => root.removeAttribute("data-theme-panel");
  }, [open, isMobile, side]);

  if (!open) return null;

  // ── Compute panel position styles ───────────────────────────────────────
  // Respects: header (top), sidebar (left on desktop), media player (bottom)
  // When sidebarExpanded=true on desktop the left edge shifts to --sidebar-width (256px).
  const isHorizontal = side === "left" || side === "right";
  const isVertical = side === "top" || side === "bottom";

  // Today's layout (Gordon 2026-11-01): the menu sits on the person's menu side
  // (right by default), the VU strip on the other edge. The panel docks against
  // whichever is on its side and the page makes room for it (globals.css,
  // html[data-theme-panel]) — like opening the menu, nothing overlaps.
  const menuW = sidebarExpanded ? "var(--sidebar-width, 256px)" : "var(--sidebar-width-icon, 50px)";
  // The VU column only while the meter shows (Gordon 2026-11-02: "open all the
  // way to the screen edge if the VU isn't playing") — globals.css sets
  // --bb-vu-col to 0 under html[data-vu-strip="off"]; the panel slides along.
  const vuW = "var(--bb-vu-col, var(--sidebar-width-icon, 50px))";
  const edge = (s: "left" | "right") => (s === menuSide ? menuW : vuW);
  const leftEdge = edge("left");

  const panelStyle: React.CSSProperties = {};
  const panelCls: string[] = [
    "fixed z-50 bg-background border-border/60 shadow-2xl flex focus:outline-none transition-[left,right] duration-200",
    "animate-in duration-300",
  ];

  if (isMobile) {
    // Mobile: full-width panel from header to bottom, no sidebar offsets
    panelCls.push("flex-col border-t slide-in-from-bottom");
    panelStyle.top = "var(--header-height, 50px)";
    panelStyle.left = "0";
    panelStyle.right = "0";
    panelStyle.height = "calc(100dvh - var(--header-height, 50px))";
    panelStyle.overflowY = "auto";
    panelStyle.zIndex = 200; // above mobile sidebar sheet
  } else if (side === "right") {
    panelCls.push("flex-col border-l slide-in-from-right");
    panelStyle.top = "var(--header-height, 50px)";
    panelStyle.right = edge("right");
    panelStyle.height = "calc(100dvh - var(--header-height, 50px) - var(--player-height, 0px))";
    panelStyle.width = THEME_PANEL_W;
  } else if (side === "left") {
    panelCls.push("flex-col border-r slide-in-from-left");
    panelStyle.top = "var(--header-height, 50px)";
    panelStyle.left = edge("left");
    panelStyle.height = "calc(100dvh - var(--header-height, 50px) - var(--player-height, 0px))";
    panelStyle.width = THEME_PANEL_W;
  } else if (side === "top") {
    panelCls.push("flex-col border-b slide-in-from-top");
    panelStyle.top = "var(--header-height, 50px)";
    panelStyle.left = leftEdge;
    panelStyle.right = edge("right");
    panelStyle.height = "calc(100dvh - var(--header-height, 50px))";
    panelStyle.overflowY = "auto";
  } else if (side === "bottom") {
    panelCls.push("flex-col border-t slide-in-from-bottom");
    panelStyle.bottom = "var(--player-height, 0px)";
    panelStyle.left = leftEdge;
    panelStyle.right = edge("right");
    panelStyle.height = "calc(100dvh - var(--header-height, 50px) - var(--player-height, 0px))";
    panelStyle.overflowY = "auto";
  }


  const scalePercent = Math.round(fontScale * 100);

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className={cn(...panelCls)}
      style={panelStyle}
    >
      {/* ── Panel Header ─────────────────────────────────────────────────── */}
      <div className="px-4 pt-3 pb-2 border-b border-border/60 flex-shrink-0 space-y-2">

        {/* ── Random Theme + Close, one row, one height (Gordon 2026-11-02:
            the close X up in the icon row was "too hidden") ──────────────── */}
        <div className="flex items-center gap-2">
        <button
          onClick={handleRandomTheme}
          className={cn(
            "flex-1 min-w-0 flex items-center justify-center gap-2.5 h-10 rounded-xl font-semibold text-sm",
            "transition-all duration-200 active:scale-[0.97]",
            "border border-border/60 text-foreground hover:border-primary/60 hover:text-primary bg-transparent"
          )}
        >
          <Shuffle className="h-4 w-4 flex-shrink-0" />
          Random Theme
          <span className="ml-auto mr-1 text-[10px] font-normal opacity-75 hidden sm:inline">
            {randomIsGif ? tSettings("themeNextBackground") : tSettings("themeNextColour")}
          </span>
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label={tCommon("close")}
          title={tCommon("close")}
          className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-xl border border-border/60 text-foreground hover:border-primary/60 hover:text-primary bg-transparent transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-base font-semibold">
            <Palette className="h-4 w-4 text-primary" />
            Themes &amp; Appearance
          </div>
          <div className="flex items-center gap-1">
            {/* Position toggles */}
            {(["left", "right", "top", "bottom"] as PanelSide[]).map((s) => (
              <TooltipProvider key={s}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={side === s ? "secondary" : "ghost"}
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleSide(s)}
                    >
                      {SIDE_ICONS[s]}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Dock {s}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
            <div className="w-px h-4 bg-border mx-1" />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleRandomTheme}>
                    <Shuffle className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Random theme (alternates colour / GIF skin)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleExport}>
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Export theme JSON</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* ── Font Size Slider ───────────────────────────────────────────── */}
        <div className="flex items-center gap-2">
          <Type className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className="text-xs text-muted-foreground flex-shrink-0 w-7">Aa</span>
          <input
            type="range"
            min={0.75}
            max={1.5}
            step={0.05}
            value={fontScale}
            onChange={(e) => handleFontScale(parseFloat(e.target.value))}
            className="flex-1 h-1.5 accent-primary cursor-pointer"
          />
          <span className="text-xs text-muted-foreground flex-shrink-0 w-9 text-right">Aa</span>
          <button
            className="text-[10px] text-muted-foreground bg-muted hover:bg-muted/80 px-1.5 py-0.5 rounded flex-shrink-0"
            onClick={() => handleFontScale(1)}
            title="Reset font size"
          >
            {scalePercent}%
          </button>
        </div>

        {/* ── Button Shape ────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground flex-shrink-0 w-16">Buttons</span>
          <div className="flex gap-1.5 flex-1">
            {BTN_SHAPE_PRESETS.map((p) => (
              <button
                key={p.value}
                onClick={() => handleBtnRadius(p.value)}
                title={p.label}
                className={cn(
                  "flex-1 flex items-center justify-center py-1.5 text-[10px] font-medium border transition-all",
                  p.preview, // apply the visual shape to the button itself
                  btnRadius === p.value
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted text-muted-foreground border-border hover:border-foreground/30 hover:text-foreground"
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Contrast vectors overlay (moved here from the floating button) ── */}
        <div className="flex items-center justify-between">
          <Label htmlFor="vector-overlay" className="text-xs text-muted-foreground cursor-pointer">
            Contrast vectors
          </Label>
          <Switch id="vector-overlay" checked={vectorOn} onCheckedChange={handleVectorToggle} />
        </div>

        {/* Color rotation toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
            <Label htmlFor="color-rotation" className="text-xs text-muted-foreground cursor-pointer">
              Color rotation
            </Label>
          </div>
          <Switch
            id="color-rotation"
            checked={colorRotationEnabled}
            onCheckedChange={setColorRotationEnabled}
            className="scale-75"
          />
        </div>

        {/* Background demo: backdrops drift slowly and change by themselves (Gordon 2026-11-02). */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2" title={tSettings("themeBgDemoHint")}>
            <Film className="h-3.5 w-3.5 text-muted-foreground" />
            <Label htmlFor="bg-demo" className="text-xs text-muted-foreground cursor-pointer">
              {tSettings("themeBgDemo")}
            </Label>
          </div>
          <Switch id="bg-demo" checked={bgDemo} onCheckedChange={setBgDemo} className="scale-75" aria-describedby="bg-demo-hint" />
          <span id="bg-demo-hint" className="sr-only">{tSettings("themeBgDemoHint")}</span>
        </div>

        {/* Dark / Light / System toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {themeMode === 'dark' ? (
              <Moon className="h-3.5 w-3.5 text-muted-foreground" />
            ) : themeMode === 'light' ? (
              <Sun className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <span className="text-xs text-muted-foreground">Appearance</span>
          </div>
          <div className="flex items-center gap-0.5 bg-muted/50 rounded-md p-0.5">
            {(['dark', 'system', 'light'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => handleThemeMode(mode)}
                className={cn(
                  "flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-colors",
                  themeMode === mode
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {mode === 'dark' && <Moon className="h-3 w-3" />}
                {mode === 'system' && <Monitor className="h-3 w-3" />}
                {mode === 'light' && <Sun className="h-3 w-3" />}
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search themes…"
            className="pl-8 h-8 text-xs !text-foreground !bg-background placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {/* ── Category tabs ─────────────────────────────────────────────────── */}
      <Tabs
        value={activeCategory}
        onValueChange={(v) => { setActiveCategory(v as typeof activeCategory); setSearch(""); }}
        className="flex flex-col flex-1 min-h-0"
      >
        <div className="px-4 pt-2 pb-1.5 border-b border-border/40 flex-shrink-0">
          <TabsList className="flex flex-wrap gap-1.5 bg-transparent !h-auto p-0 w-full items-start">
              <TabsTrigger
                value="all"
                className="text-xs px-2.5 py-1 h-7 rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                All ({themePresets.length})
              </TabsTrigger>
              {THEME_CATEGORIES.map((cat) => {
                const count = themePresets.filter((p) => p.category === cat.id).length;
                return (
                  <TabsTrigger
                    key={cat.id}
                    value={cat.id}
                    className="text-xs px-2.5 py-1 h-7 rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    <CategoryIcon id={cat.id} className="mr-1 -mt-0.5" />{cat.label}
                    <span className="ml-1 opacity-60">({count})</span>
                  </TabsTrigger>
                );
              })}
              <TabsTrigger
                value="custom"
                className="text-xs px-2.5 py-1 h-7 rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                Custom
              </TabsTrigger>
              <TabsTrigger
                value="gif"
                className="text-xs px-2.5 py-1 h-7 rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Film className="h-3 w-3 mr-1" />
                Image BG
              </TabsTrigger>
          </TabsList>
        </div>

        {/* ── GIF Background Tab ────────────────────────────────────────── */}
        {activeCategory === "gif" && (
          <div className="flex-1 min-h-0 overflow-hidden">
            <GifThemePanel
              enabled={gifThemeEnabled}
              library={gifLibrary}
              sectionGifs={sectionGifs}
              onToggle={setGifThemeEnabled}
              onUpdateLibrary={updateGifLibrary}
              onSetSectionGif={setSectionGif}
              onClearSectionGifs={clearSectionGifs}
              onRandomize={randomizeGifSections}
              userId={user?.uid}
              isSuperAdmin={isSuperAdmin}
              gifSkins={gifSkins}
              activeSkinId={activeSkinId}
              onApplySkin={applyGifSkin}
              onClearSkin={clearGifSkin}
              gifOpacity={gifOpacity}
              gifBgColor={gifBgColor}
              gifTextColor={gifTextColor}
              gifBold={gifBold}
              onSetGifOpacity={setGifOpacity}
              onSetGifBgColor={setGifBgColor}
              onSetGifTextColor={setGifTextColor}
              onSetGifBold={setGifBold}
              gifSize={gifSize}
              onSetGifSize={setGifSize}
              gifTileSize={gifTileSize}
              onSetGifTileSize={setGifTileSize}
            />
          </div>
        )}

        {/* ── Preset grid / Custom builder ───────────────────────────────── */}
        {activeCategory !== "gif" && (
        <TabsContent value={activeCategory} className="flex-1 min-h-0 mt-0 overflow-hidden" forceMount>
          {activeCategory === "custom" ? (
            <ScrollArea className="h-full">
              <div className="px-4 py-4">
                <CustomThemeBuilder />
              </div>
            </ScrollArea>
          ) : (
            <ScrollArea className="h-full">
              <div className="px-4 pt-3 pb-10">
                {filteredPresets.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground text-sm">
                    No themes match &ldquo;{search}&rdquo;
                  </div>
                ) : (
                  <>
                    {activeCategory === "all" && !search
                      ? THEME_CATEGORIES.map((cat) => {
                          const group = filteredPresets.filter((p) => p.category === cat.id);
                          if (group.length === 0) return null;
                          return (
                            <div key={cat.id} className="mb-5">
                              <div className="flex items-center gap-1.5 mb-2.5">
                                <CategoryIcon id={cat.id} className="text-muted-foreground" />
                                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                  {cat.label}
                                </p>
                                <Badge variant="secondary" className="text-[10px] h-4 px-1.5">{group.length}</Badge>
                              </div>
                              <div
                                className="grid gap-2"
                                style={{ gridTemplateColumns: isVertical ? "repeat(auto-fill, minmax(160px, 200px))" : "repeat(2, 1fr)" }}
                              >
                                {group.map((preset) => (
                                  <ThemeSwatch
                                    key={preset.id}
                                    preset={preset}
                                    isActive={currentTheme?.id === preset.id}
                                    onSelect={() => handleSelect(preset)}
                                    isLoading={applying === preset.id}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })
                      : (
                        <div
                          className="grid gap-2"
                          style={{ gridTemplateColumns: isVertical ? "repeat(auto-fill, minmax(160px, 200px))" : "repeat(2, 1fr)" }}
                        >
                          {filteredPresets.map((preset) => (
                            <ThemeSwatch
                              key={preset.id}
                              preset={preset}
                              isActive={currentTheme?.id === preset.id}
                              onSelect={() => handleSelect(preset)}
                              isLoading={applying === preset.id}
                            />
                          ))}
                        </div>
                      )}
                  </>
                )}
              </div>
            </ScrollArea>
          )}
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ─── Trigger button (sidebar embed) ───────────────────────────────────────
export function ThemePickerButton({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", className)}
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle theme picker"
            >
              <Palette className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Themes &amp; Appearance</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <ThemePicker open={open} onClose={() => setOpen(false)} />
    </>
  );
}
